import { useEffect } from "react";
import { Outlet, Route, Routes } from "react-router";
import biApi from "../../../../api/biApi";
import useClientContext from "../../../../context/useClientContext";
import { GroupsContextProvider } from "../report-lists/groups/contexts/GroupsContextProvider";
import {
  getEditSystemReportUrl,
  getSystemReportsUrl,
  getViewSystemReportUrl,
} from "../report-lists/reports/utilities/editReportUrl";
import { ReportType } from "../../../../api/types/svTypes.ts";
import SmartViewAppPage from "./SmartViewAppPage";
import { chartRelatedReportTypes } from "./utilities/chartRelatedReport.types.ts";
import { EditRoot } from "./components/builder/EditRoot";
import UiViewRoot from "./components/builder/ViewRoot";
import useReportGroups from "./components/builder/common/hooks/useReportGroups";
import ImportantDataLoadingFailed from "./components/common/ImportantDataLoadingFailed";
import type { BiApiClient } from "./contexts/ApiClientProviderContext";
import { BiApiClientProvider } from "./contexts/BiApiClientProvider";
import useReportMode from "./hooks/useReportMode";
import { useAppDispatch } from "./store/hooks/useAppDispatch";
import { fieldsStateActions } from "./store/thunks/fieldStatesThunks";

const supportedReportTypes = [ReportType.Pivot, ReportType.Tabular, ...chartRelatedReportTypes, ReportType.Tile];

const SmartViewSystemReportPage = () => {
  return (
    <Routes>
      <Route element={<SmartViewAppPage supportedReportTypes={supportedReportTypes} />}>
        <Route element={<SystemReportsContainer />}>
          <Route path=":id/view" element={<UiViewRoot />} />
          <Route path=":id/edit" element={<EditRoot />} />
        </Route>
      </Route>
    </Routes>
  );
};

const SystemReportsContainer = () => {
  const { clientCode } = useClientContext();
  const { mode } = useReportMode();
  const dispatch = useAppDispatch();
  const { reportGroups, actions, ui } = useReportGroups({ apiClient: biApi.systemReports });

  useEffect(() => {
    if (mode === "view") {
      //The mode can be changed from edit to run, so we need to reset the fields state
      dispatch(fieldsStateActions.resetFieldsState());
    }
  }, [mode, dispatch]);

  if (ui.error) {
    return (
      <ImportantDataLoadingFailed
        title="Failed to load the report"
        disclaimer="There appears to have been an issue with loading the data. Kindly attempt to reload the report. Should the issue persist, please get in touch with our support team for further assistance."
      />
    );
  }

  return (
    <BiApiClientProvider<BiApiClient>
      api={{
        updateReport: biApi.systemReports.updateReport,
        addReport: biApi.systemReports.addReport,
        createDraftReport: biApi.systemReports.createDraftReport,
        getReport: biApi.systemReports.getReport,
        runTabularReport: biApi.systemReports.runTabularReport,
        buildTabularReport: biApi.systemReports.buildTabularReport,
        buildTileReport: biApi.systemReports.buildTileReport,
        buildChartReport: biApi.systemReports.buildChartReport,
        runPivotReport: biApi.systemReports.runPivotReport,
        buildPivotReport: biApi.systemReports.buildPivotReport,
        changeReportGroup: biApi.systemReports.changeReportGroup,
        renameReport: biApi.systemReports.renameReport,
        deleteReports: biApi.systemReports.deleteReports,
        getReportAuthorizationInfo: biApi.systemReports.getReportAuthorizationInfo,
        getReports: biApi.systemReports.getReports,
        getReportConfigurationForRunning: biApi.systemReports.getReportConfigurationForRunning,
        validateName: biApi.systemReports.validateReportName,
        getReportSharing: biApi.systemReports.getReportSharing,
        updateReportSharing: biApi.systemReports.updateReportSharing,
        getEditReportUrl: getEditSystemReportUrl,
        getViewReportUrl: getViewSystemReportUrl,
        getReportsUrl: getSystemReportsUrl,
        requestRunTabularExport: biApi.systemReports.requestRunTabularExport,
        requestBuildTabularExport: biApi.systemReports.requestBuildTabularExport,
        requestRunPivotExport: biApi.systemReports.requestRunPivotExport,
        requestBuildPivotExport: biApi.systemReports.requestBuildPivotExport,
        getReportHistory: biApi.systemReports.getReportHistory,
      }}
    >
      <GroupsContextProvider groups={{ [clientCode]: reportGroups }} ui={ui} actions={actions}>
        <Outlet />
      </GroupsContextProvider>
    </BiApiClientProvider>
  );
};

export default SmartViewSystemReportPage;
